/**
 * Chase rig: fixed lateral framing (no strafe with lane changes). Portrait uses flatter, nearer aim so the
 * hero stays well above the phone’s bottom bezel — tall bird angles had pushed the rig to a thin line.
 */

import * as THREE from "three";

/** Desktop / landscape — moderate chase height and down-range view. */
const ELEVATION_DESKTOP = THREE.MathUtils.degToRad(40);
/** Portrait — shallower than 46° so the cab stays in the lower–middle of the screen. */
const ELEVATION_PORTRAIT = THREE.MathUtils.degToRad(36);

const ORBIT_DESKTOP = 12.15;
/** Tighter on phone: bigger hero read above the circular lane buttons. */
const ORBIT_PORTRAIT = 10.85;

const LOOK_AHEAD_DESKTOP = 32;
/** Nearer aim on narrow aspect — long look-ahead shoved the mesh under the frustum. */
const LOOK_AHEAD_PORTRAIT = 21;

/**
 * Lane parallax — keep `0` so guardrails / stripes don’t slide sideways with lane changes.
 */
const CAM_X_TRACK = 0;

/** Look target lateral — `0` for road-locked framing. */
const LOOK_X_BIAS = 0;

const LOOK_Y_DESKTOP = 0.05;
/** Slightly higher aim on portrait so we’re not sighting past the truck toward the horizon only. */
const LOOK_Y_PORTRAIT = 0.14;

const LOOK_Z_NUDGE_DESKTOP = 0.32;
const LOOK_Z_NUDGE_PORTRAIT = 0.38;

const CAMERA_HEIGHT_SKIM_DESKTOP = 0.22;
const CAMERA_HEIGHT_SKIM_PORTRAIT = 0.18;

const CAMERA_Y_BOOST_DESKTOP = 0.88;
/** Less vertical lift on phones than desktop — pairs with lower elevation. */
const CAMERA_Y_BOOST_PORTRAIT = 0.62;

const FOV_PORTRAIT = 49;
const FOV_DESKTOP = 47;

const tmpLook = new THREE.Vector3();

/**
 * @param {THREE.PerspectiveCamera} camera
 * @param {THREE.Vector3} playerPos — chassis anchor
 * @param {number} smoothCamX — persistent camera X (mutated)
 * @param {number} dt
 */
export function updateObliqueChaseCamera(camera, playerPos, smoothCamX, dt) {
  const portrait = camera.aspect < 1;

  const elevation = portrait ? ELEVATION_PORTRAIT : ELEVATION_DESKTOP;
  const orbit = portrait ? ORBIT_PORTRAIT : ORBIT_DESKTOP;
  const lookAhead = portrait ? LOOK_AHEAD_PORTRAIT : LOOK_AHEAD_DESKTOP;
  const lookY = portrait ? LOOK_Y_PORTRAIT : LOOK_Y_DESKTOP;
  const lookZNudge = portrait ? LOOK_Z_NUDGE_PORTRAIT : LOOK_Z_NUDGE_DESKTOP;
  const heightSkim = portrait ? CAMERA_HEIGHT_SKIM_PORTRAIT : CAMERA_HEIGHT_SKIM_DESKTOP;
  const yBoost = portrait ? CAMERA_Y_BOOST_PORTRAIT : CAMERA_Y_BOOST_DESKTOP;

  camera.fov = portrait ? FOV_PORTRAIT : FOV_DESKTOP;

  const horiz = Math.cos(elevation) * orbit;
  let vert = Math.sin(elevation) * orbit;
  vert = Math.max(0.85, vert - heightSkim);

  const targetCamX = playerPos.x * CAM_X_TRACK;
  const nextX = smoothCamX + (targetCamX - smoothCamX) * Math.min(1, dt * 4);

  camera.position.set(nextX, playerPos.y + vert + yBoost, playerPos.z + horiz);
  camera.updateProjectionMatrix();

  tmpLook.set(
    playerPos.x * LOOK_X_BIAS,
    playerPos.y + lookY,
    playerPos.z - lookAhead + lookZNudge
  );
  camera.lookAt(tmpLook);

  return nextX;
}
